import React, { forwardRef } from 'react';
import useTheme from '@polestar/component-warehouse-react/hooks/useTheme';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { minimalHitbox, minimalHitboxHeight, minimalHitboxWidth, } from '@polestar/component-warehouse-react/helpers/style';
import MinimalHitboxTypes from '@polestar/component-warehouse-react/tokens/minimal-hitbox-types/MinimalHitboxTypes';
import Alignments from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import FontWeights from '@polestar/component-warehouse-react/tokens/font-weights/FontWeights';
import Fonts from '@polestar/component-warehouse-react/tokens/fonts/Fonts';
import FontSizes from '@polestar/component-warehouse-react/tokens/font-sizes/FontSizes';
import LineHeights from '@polestar/component-warehouse-react/tokens/line-heights/LineHeights';
import { useButton } from '@polestar/component-warehouse-react/components/button/Button.helpers';
import { ButtonMotionContextProvider } from '@polestar/component-warehouse-react/components/button/internal/buttonMotion';
export var IconButtonVariants;
(function (IconButtonVariants) {
    IconButtonVariants["white"] = "white";
    IconButtonVariants["black"] = "black";
    IconButtonVariants["transparent"] = "transparent";
    IconButtonVariants["followTheme"] = "followTheme";
})(IconButtonVariants || (IconButtonVariants = {}));
const IconButton = forwardRef(({ icon, size = Sizes.medium, variant = IconButtonVariants.transparent, minimalHitboxType = MinimalHitboxTypes.both, pairedWithText = false, disabled = false, badge, iconAnimation = false, ...props }, ref) => {
    const theme = useTheme();
    const { iconSize, buttonStyles } = gatherSizes({ theme, size, variant, pairedWithText, icon });
    const { active, hover, ...buttonEvents } = useButton(props);
    return (React.createElement(ButtonMotionContextProvider, { active: active, hover: hover, iconAnimation: iconAnimation },
        React.createElement(ButtonContainer, { ref: ref, title: props === null || props === void 0 ? void 0 : props['aria-label'], type: "button", ...props, ...buttonStyles, minimalHitboxType: minimalHitboxType, disabled: disabled, variant: variant, size: size, ...(!!badge
                ? {
                    width: undefined,
                }
                : {}), ...buttonEvents },
            React.createElement(ButtonContent, { icon: icon, iconSize: iconSize, size: size, variant: variant, pairedWithText: pairedWithText, badge: badge }))));
});
IconButton.displayName = 'IconButton';
export default IconButton;
export const IconAnchorButton = forwardRef(({ icon, size = Sizes.medium, variant = IconButtonVariants.transparent, minimalHitboxType = MinimalHitboxTypes.both, pairedWithText = false, disabled = false, badge, ...props }, ref) => {
    const theme = useTheme();
    const { iconSize, buttonStyles } = gatherSizes({ theme, size, variant, pairedWithText, icon });
    if (props.target && props.target === '_blank' && props.rel === undefined) {
        console.warn('[IconAnchorButton] Please provide `rel = "noopener" | "noreferrer"`, because of security considerations when using `target="_blank"`. More information at https://web.dev/external-anchors-use-rel-noopener/');
    }
    return (React.createElement(AnchorContainer, { ref: ref, title: props === null || props === void 0 ? void 0 : props['aria-label'], ...props, ...buttonStyles, minimalHitboxType: minimalHitboxType, disabled: disabled, variant: variant, size: size, ...(!!badge
            ? {
                width: undefined,
            }
            : {}) },
        React.createElement(ButtonContent, { icon: icon, iconSize: iconSize, size: size, variant: variant, pairedWithText: pairedWithText, badge: badge })));
});
IconAnchorButton.displayName = 'IconAnchorButton';
const ButtonContent = ({ icon, iconSize, size = Sizes.medium, variant, pairedWithText, badge, }) => {
    return (React.createElement(Wrapper, null,
        React.createElement(StyledIcon, { icon: icon, size: IconSizes[iconSize], sizeValue: size, "aria-hidden": true, variant: variant, pairedWithText: pairedWithText, topOffset: false, hasTrailingIcon: !!badge }),
        badge));
};
const skipForwardProps = shouldNotForwardProps(false, 'spacing', 'minimalHitboxType', 'alignmentOffset', 'size', 'width', 'height', 'variant', 'visible');
const AnchorContainer = styled('a', skipForwardProps)((props) => sharedIconButtonStyles(props));
const ButtonContainer = styled('button', skipForwardProps)((props) => sharedIconButtonStyles(props));
const gatherSizes = ({ theme, variant, size, pairedWithText, icon, }) => {
    const { sizes } = theme.iconButton[variant];
    return {
        iconSize: pairedWithText && icon.textPairingGlyph
            ? Sizes.medium
            : size === Sizes.medium
                ? Sizes.small
                : size === Sizes.xLarge
                    ? Sizes.large
                    : Sizes.medium,
        buttonStyles: sizes[size],
    };
};
/**
 * Useful for aligning the component to content below or above but also at the start / end of a Section.
 */
export const iconButtonIconOffset = (size, alignmentOffset) => size === Sizes.medium
    ? alignmentOffset !== undefined
        ? Spacings.xSmall
        : Spacings.xxSmall
    : Spacings.small;
const sharedIconButtonStyles = ({ theme, height, width, variant, minimalHitboxType, alignmentOffset, size, disabled = false, }) => {
    const offset = iconButtonIconOffset(size, alignmentOffset);
    return css(css `
      margin: 0;
      display: inline-block;
      color: ${theme.iconButton[variant].states.enabled.color};
      border: 0;
      cursor: pointer;
      width: auto;
      max-width: 100%;
      box-shadow: none;
      background-color: ${theme.iconButton[variant].backgroundColor};
      transition: ${createCSSTransition('background-color', MotionEasings.default, MotionDurations.fast)},
        ${createCSSTransition('color', MotionEasings.default, MotionDurations.fast)},
        ${createCSSTransition('border-color', MotionEasings.default, MotionDurations.fast)},
        ${createCSSTransition('box-shadow', MotionEasings.default, MotionDurations.fast)},
        ${createCSSTransition('opacity', MotionEasings.default, MotionDurations.fast)};
      text-decoration: none;
      user-select: none;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
    `, 
    // to ensure nothing in the parent nodes are conflicting the presentation of the icons inside this button
    css `
      font-family: ${Fonts.polestarUnica};
      font-weight: ${FontWeights.regular};
      font-size: ${FontSizes.body};
      line-height: ${LineHeights.small};
    `, css `
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${width || 'auto'};
      height: ${height};
      padding: 0;

      @media (hover: hover) {
        &:hover {
          color: ${theme.iconButton[variant].states.hover.color};
        }
      }

      &:active {
        color: ${theme.iconButton[variant].states.active.color};
      }
    `, variant !== IconButtonVariants.transparent &&
        css `
        border-radius: 50%;
      `, !width &&
        css `
        overflow: visible;
      `, minimalHitboxType === MinimalHitboxTypes.both && minimalHitbox, minimalHitboxType === MinimalHitboxTypes.width && minimalHitboxWidth, minimalHitboxType === MinimalHitboxTypes.height && minimalHitboxHeight, alignmentOffset === Alignments.left &&
        css `
        margin-inline-start: -${offset};
      `, alignmentOffset === Alignments.right &&
        css `
        margin-inline-end: -${offset};
      `, disabled &&
        css `
        pointer-events: none;
        color: ${theme.iconButton[variant].states.disabled.color};
      `);
};
const Wrapper = styled.div `
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const StyledIcon = styled(Icon, shouldNotForwardProps(false, 'sizeValue'))(({ theme, variant, sizeValue = Sizes.medium, hasTrailingIcon }) => css(css `
      display: flex;
      position: relative;
      z-index: 1;
    `, sizeValue &&
    hasTrailingIcon &&
    css `
        padding-inline-end: ${theme.iconButton[variant].sizes[sizeValue].spacing};
      `));
